import classNames from "classnames";
import "./styles.scss";
import { Box, Button, Stack, Typography } from "@mui/material";
import { PaymentFrequency } from "../../__generated__/types";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { useMemo } from "react";
import { Link } from "react-router";

export const SubscriptionCard = ({
  title,
  features,
  plainFeatures,
  selected,
  paymentFrequency,
  description,
  price,
  className,
  originalPrice,
  isBeta,
  selectable = true,
  actionButtonLabel,
  actionButtonTo,
  actionButtonHref,
}: {
  title: string;
  features: string[];
  plainFeatures?: string[];
  selected: boolean;
  paymentFrequency: PaymentFrequency;
  description?: string;
  price: number | string;
  originalPrice?: number | string;
  className?: string;
  isBeta?: boolean;
  selectable?: boolean;
  actionButtonLabel?: string;
  actionButtonTo?: string;
  actionButtonHref?: string;
}) => {
  const formatPrice = (value: number | string | undefined): string => {
    if (value === undefined || value === null) {
      return '';
    }

    if (typeof value === 'string' && value.trim() === '') {
      return '';
    }

    const numericValue = typeof value === 'number' ? value : Number(value);

    if (Number.isNaN(numericValue)) {
      return typeof value === 'string' ? value : numericValue.toString();
    }

    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(numericValue);
  };

  const iconMarkup =
    '<span class="hl-list-icon"><svg class="MuiSvgIcon-root MuiSvgIcon-fontSizeMedium text-green css-1umw9bq-MuiSvgIcon-root" focusable="false" aria-hidden="true" viewBox="0 0 24 24" data-testid="CheckCircleIcon"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2m-2 15-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8z"></path></svg></span>';

  const enhancedDescription = useMemo(() => {
    if (!description) {
      return '';
    }

    return description.replace(/<li([^>]*)>/gi, `<li$1>${iconMarkup}`);
  }, [description, iconMarkup]);

  return (
    <Box
      className={classNames(
        "subscription-card text-left",
        selected && "active",
        className
      )}
      sx={{
        padding: "24px",
        display: "flex",
        flexDirection: "column",
        height: "100%",
      }}
    >
      <Stack direction={"row"} spacing={2} justifyContent={"space-between"}>
        <Typography
          variant="h5"
          component="h3"
          pt="20px"
          mb="16px !important"
          className="title"
          sx={{ color: '#d22268' }}
        >
          {title}
        </Typography>
        {selectable && (
          <div className="select-circle" style={{ flexShrink: 0 }}>
            <div className="inner-circle" />
          </div>
        )}
      </Stack>
      <Stack direction="column" className="price-container">
        <Stack direction={"row"} spacing={1} sx={{ mt: 1 }}>
          {isBeta && originalPrice && (
            <Typography
              variant="h6"
              component="span"
              className="original-price"
            >
              ${formatPrice(originalPrice)}
            </Typography>
          )}
          <Typography variant="h6" component="span">
            ${formatPrice(price)}
          </Typography>
        </Stack>
        <Typography variant="body2" className="billing-interval" mb="16px">
          {Number(price) === 0
            ? 'Free'
            : paymentFrequency === PaymentFrequency.Annually
            ? 'Total Annual Fee'
            : paymentFrequency === PaymentFrequency.Monthly
            ? 'Billed Monthly'
            : 'Billed Bi-Annually'}
        </Typography>
      </Stack>
      {description && (
        <Typography
          variant="body1"
          className="description"
          mb="16px"
          sx={{
            '& h1, & h2, & h3, & h4, & h5, & h6': {
              fontFamily: 'inherit',
              fontWeight: 600,
              marginBottom: '12px',
            },
            '& p': {
              marginBottom: '12px',
            },
            '& ul': {
              listStyle: 'none',
              paddingLeft: 0,
              margin: '0 0 12px',
              display: 'flex',
              flexDirection: 'column',
              gap: '8px',
            },
            '& li': {
              position: 'relative',
              marginBottom: 0,
              display: 'flex',
              alignItems: 'flex-start',
              gap: '12px',
              lineHeight: 1.6,
              color: '#374151',
            },
            '& li p': {
              margin: 0,
              lineHeight: 1.6,
            },
            '& .hl-list-icon': {
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'center',
              width: '20px',
              height: '20px',
              minWidth: '20px',
              marginTop: '2px',
              flexShrink: 0,
            },
            '& .hl-list-icon svg': {
              width: '20px',
              height: '20px',
              display: 'block',
              flexShrink: 0,
            },
          }}
          dangerouslySetInnerHTML={{ __html: enhancedDescription }}
        />
      )}
      <ul className="subscription-feature-list">
        {features?.map((value: string, index: number) => (
          <li key={`${value}-${index}`} className="subscription-feature-item">
            <CheckCircleIcon className="subscription-feature-icon" />
            <Typography variant="body1" className="subscription-feature-copy">
              {value}
            </Typography>
          </li>
        ))}
      </ul>
      {plainFeatures && plainFeatures.length > 0 && (
        <Stack spacing={0.75} sx={{ mt: 1 }}>
          {plainFeatures.map((value: string, index: number) => (
            <Typography
              key={`${value}-${index}`}
              variant="body2"
              sx={{ color: "#374151", lineHeight: 1.6 }}
            >
              {value}
            </Typography>
          ))}
        </Stack>
      )}
      {actionButtonLabel && (actionButtonTo || actionButtonHref) && (
        <Box sx={{ mt: "auto", pt: 3 }}>
          {actionButtonTo ? (
            <Button
              fullWidth
              variant="contained"
              component={Link}
              to={actionButtonTo}
              onClick={(event) => event.stopPropagation()}
              sx={{
                py: "12px",
                fontWeight: 700,
                letterSpacing: "0.02em",
                backgroundColor: "#D22268",
                "&:hover": {
                  backgroundColor: "#B91D59",
                },
              }}
            >
              {actionButtonLabel}
            </Button>
          ) : (
            <Button
              fullWidth
              variant="contained"
              href={actionButtonHref}
              onClick={(event) => event.stopPropagation()}
              sx={{
                py: "12px",
                fontWeight: 700,
                letterSpacing: "0.02em",
                backgroundColor: "#D22268",
                "&:hover": {
                  backgroundColor: "#B91D59",
                },
              }}
            >
              {actionButtonLabel}
            </Button>
          )}
        </Box>
      )}
    </Box>
  );
};
